// @@PORTAL_INVOICES_v83@@ Client portal — Invoices tab.
// Data: GET /api/portal/invoices?client_id= (server-scoped to this login's
// clients). Year + status filtering and sorting are client-side over that one
// list. Downloads are blob fetches via apiFetch (a plain <a href> would skip
// the Bearer header) of Zoho's native invoice PDF.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import PayInvoices from './PayInvoices'; // @@PORTAL_PAY_v124@@

function money(n, ccy) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD' }).format(Number(n));
  } catch (e) {
    return `$${Number(n).toFixed(2)}`;
  }
}
// YYYY-MM-DD -> local date. Never new Date('YYYY-MM-DD') (UTC -> shows the day before).
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
}
const STATUS_LABEL = { open: 'Open', overdue: 'Overdue', partially_paid: 'Partially paid', paid: 'Paid' };
const OPENISH = new Set(['open', 'overdue', 'partially_paid']);
const FILTERS = [['open', 'Open'], ['overdue', 'Overdue'], ['paid', 'Paid'], ['all', 'All']];
const COLS = [
  { key: 'number', label: 'Invoice #' },
  { key: 'date', label: 'Date' },
  { key: 'due_date', label: 'Due' },
  { key: 'total', label: 'Total', right: true },
  { key: 'balance', label: 'Balance due', right: true },
  { key: 'status', label: 'Status' },
];
const NUMERIC = new Set(['total', 'balance']);
const safeName = (s) => String(s || '').replace(/[^A-Za-z0-9._-]/g, '_');

export default function InvoicesTab({ clientId, autoPay, onAutoPayDone /* @@PORTAL_PAY_v124_1@@ */ }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('all');
  const [filter, setFilter] = useState('open');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [reloadKey, setReloadKey] = useState(0); // @@PORTAL_PAY_v124@@ bumped after a payment

  useEffect(() => {
    if (!clientId) return;
    setLoading(true); setData(null); setYear('all'); setMsg('');
    apiFetch(`/api/portal/invoices?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setData(d))
      .catch(() => setData({ available: false, reason: 'error' }))
      .finally(() => setLoading(false));
  }, [clientId, reloadKey]);

  const ccy = (data && data.currency_code) || 'USD';

  const rows = useMemo(() => {
    if (!data || !data.available) return [];
    let list = (data.invoices || []).slice();
    if (year !== 'all') list = list.filter((i) => String(i.date || '').slice(0, 4) === year);
    if (filter === 'open') list = list.filter((i) => OPENISH.has(i.status));
    else if (filter !== 'all') list = list.filter((i) => i.status === filter);
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls always last
      if (bv == null) return -1;
      if (NUMERIC.has(key)) return (Number(av) - Number(bv)) * mul;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mul;
    });
    return list;
  }, [data, year, filter, sort]);

  const totals = useMemo(() => rows.reduce((t, i) => ({
    total: t.total + (Number(i.total) || 0),
    balance: t.balance + (Number(i.balance) || 0),
  }), { total: 0, balance: 0 }), [rows]);

  function toggleSort(key) {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: (key === 'number' || key === 'status') ? 'asc' : 'desc' }));
  }

  async function download(number) {
    setBusy(number); setMsg('');
    try {
      const res = await apiFetch(`/api/portal/invoices/pdf?client_id=${encodeURIComponent(clientId)}&number=${encodeURIComponent(number)}`);
      if (!res.ok) { setMsg(res.status === 404 ? `Invoice ${number} was not found.` : 'Could not download the invoice right now. Please try again shortly.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const desig = safeName(data && data.client && data.client.designation_code);
      a.href = url; a.download = 'XYZ-Invoice-' + safeName(number) + (desig ? '-' + desig : '') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('Could not download the invoice right now. Please try again shortly.');
    } finally { setBusy(''); }
  }

  const yearLabel = year === 'all' ? 'all years' : year;
  const filterLabel = { open: 'open', overdue: 'overdue', paid: 'paid', all: '' }[filter];

  return (
    <div className="cp-card">
      <div className="cp-card-head">
        <h2>Invoices</h2>
        {data && data.available && (
          <div className="cp-inv-tools">
            <select className="cp-select cp-inv-year" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Year">
              <option value="all">All years</option>
              {(data.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <div className="cp-period">
              {FILTERS.map(([id, label]) => (
                <button key={id} className={filter === id ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => setFilter(id)}>{label}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading && !data ? (
        <div className="cp-empty">Loading…</div>
      ) : !data || !data.available ? (
        <div className="cp-empty">
          {data && data.reason === 'not_linked'
            ? 'This account isn’t linked to billing yet.'
            : 'Invoices are temporarily unavailable. Please try again shortly.'}
        </div>
      ) : (
        <>
          <div className="cp-stmt-summary">
            <div><span className="cp-k">Invoices</span><span className="cp-v2">{rows.length}</span></div>
            <div><span className="cp-k">Invoiced</span><span className="cp-v2">{money(totals.total, ccy)}</span></div>
            <div><span className="cp-k">Balance due</span><span className="cp-v2">{money(totals.balance, ccy)}</span></div>
          </div>
          <PayInvoices clientId={clientId} data={data} onPaid={() => setReloadKey((k) => k + 1)} autoOpen={autoPay} onAutoOpened={onAutoPayDone} /> {/* @@PORTAL_PAY_v124@@ */}
          {msg && <div className="cp-inv-msg">{msg}</div>}
          <div className="cp-inv-scroll">
            <table className="cp-table">
              <thead>
                <tr>
                  {COLS.map((c) => (
                    <th key={c.key} className="cp-inv-th" style={c.right ? { textAlign: 'right' } : undefined}
                      onClick={() => toggleSort(c.key)} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.number}>
                    <td>{i.number}</td>
                    <td>{fmtYmd(i.date)}</td>
                    <td>{fmtYmd(i.due_date)}</td>
                    <td style={{ textAlign: 'right' }}>{money(i.total, ccy)}</td>
                    <td style={{ textAlign: 'right' }}>{money(i.balance, ccy)}</td>
                    <td><span className={'cp-inv-badge cp-inv-' + (STATUS_LABEL[i.status] ? i.status : 'open')}>{STATUS_LABEL[i.status] || i.status || '—'}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="cp-btn-sm" onClick={() => download(i.number)} disabled={busy === i.number}>
                        {busy === i.number ? 'Downloading…' : 'Download PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td className="cp-empty" colSpan={7}>No {filterLabel ? filterLabel + ' ' : ''}invoices for {yearLabel}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data.truncated && <p className="cp-sub" style={{ margin: '10px 20px' }}>Showing the most recent 2,000 invoices.</p>}
        </>
      )}
    </div>
  );
}
